/**
 * The five CSS-3D market objects used by the Market Selector coverflow.
 *
 * Pure CSS — no images beyond the market logos, no WebGL — the same approach as
 * `Cockpit/Objects3D.js`, but these are the non-collectible shapes:
 *
 *   Coin3D   crypto       — struck coin, 26px thick, milled edge, token mark
 *   Cert3D   stocks       — share certificate on a sheaf of paper
 *   Bar3D    metals       — extruded bullion bar
 *   Barrel3D crude oil     — steel drum with rolling hoops and a diamond placard
 *   Gas3D    natural gas   — pressurised cylinder with a burning jet
 *   Tower3D  real estate  — per-city building, skyline backdrop, city plate
 *   Watch3D  watches      — case, fluted bezel, dial with the brand mark
 *
 * Every object carries the same sheen strip (`.ce-ms-sheen`) so the coverflow
 * reads as one family.
 */

import React from "react";
import { shade } from "../Cockpit/fx";
import { alpha } from "./selectorMarkets";
import "./marketSelect.css";

/** Shared drop shadow that seats each object on the display. */
const OBJECT_SHADOW = "drop-shadow(0 20px 30px rgba(16,24,40,.26))";

/* ── Solids: the shared extrusion helper ────────────────────────────────── */

/**
 * A genuinely extruded solid living in the coverflow's own 3D space: front,
 * back, both sides, top and bottom, plus a ground shadow cast on the floor
 * plane (ADDENDUM-7 §3).
 *
 * The side/top/bottom faces are inset by the corner radius and carry no radius
 * of their own. Square faces spanning the full edge left the corner seam open
 * against a rounded front, and the side then read as a detached slab floating
 * beside the object.
 *
 * @param {number} w - Width
 * @param {number} h - Height
 * @param {number} d - Depth
 * @param {string} a - Material accent
 * @param {number} r - Front-face corner radius
 * @returns {object} A style per face, keyed `boxWrap`/`boxFront`/…
 */
function box(w, h, d, a, r) {
  const face = (bg) => ({ position: "absolute", left: "50%", top: "50%", background: bg, backfaceVisibility: "hidden" });
  return {
    boxWrap: {
      position: "relative", width: w + 30, height: h + 40,
      transformStyle: "preserve-3d", transform: "rotateX(8deg) rotateY(-22deg)",
    },
    boxFront: {
      ...face(shade(a, 0.2)), width: w, height: h, marginLeft: -w / 2, marginTop: -h / 2,
      borderRadius: r, transform: `translateZ(${d / 2}px)`,
    },
    boxBack: {
      ...face(shade(a, -0.6)), width: w, height: h, marginLeft: -w / 2, marginTop: -h / 2,
      borderRadius: r, transform: `translateZ(${-d / 2}px) rotateY(180deg)`,
    },
    boxRight: {
      ...face(`linear-gradient(90deg, ${shade(a, -0.08)}, ${shade(a, -0.5)})`),
      width: d, height: h - r * 2, marginLeft: -d / 2, marginTop: -(h - r * 2) / 2,
      transform: `translateX(${w / 2}px) rotateY(90deg)`,
    },
    boxLeft: {
      ...face(`linear-gradient(90deg, ${shade(a, -0.5)}, ${shade(a, -0.08)})`),
      width: d, height: h - r * 2, marginLeft: -d / 2, marginTop: -(h - r * 2) / 2,
      transform: `translateX(${-w / 2}px) rotateY(-90deg)`,
    },
    boxTop: {
      ...face(`linear-gradient(180deg, ${shade(a, 0.28)}, ${shade(a, 0.9)})`),
      width: w - r * 2, height: d, marginLeft: -(w - r * 2) / 2, marginTop: -d / 2,
      transform: `translateY(${-h / 2}px) rotateX(90deg)`,
      boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.55)}`,
    },
    boxBottom: {
      ...face(shade(a, -0.62)), width: w - r * 2, height: d,
      marginLeft: -(w - r * 2) / 2, marginTop: -d / 2,
      transform: `translateY(${h / 2}px) rotateX(-90deg)`,
    },
    boxShadow3D: {
      position: "absolute", left: "50%", top: "50%", width: w * 1.05, height: d * 1.8,
      marginLeft: -w * 0.525, marginTop: -d * 0.9, borderRadius: "50%", pointerEvents: "none",
      transform: `translateY(${h / 2 + 6}px) rotateX(90deg)`,
      background: `radial-gradient(closest-side, ${alpha("#101828", 0.42)}, transparent)`,
      filter: "blur(6px)",
    },
  };
}

/* ── Crypto: the minted coin ────────────────────────────────────────────── */

/** Which way the mark is struck, and how deep. ADDENDUM-7 §5 ships embossed. */
const COIN_RELIEF = "embossed";
const COIN_RELIEF_DEPTH = 1;

/**
 * A struck coin: 26px thick, with the edge built from 16 disc slices stepped
 * through the thickness rather than faked with an offset shadow. That wall is
 * the whole trick — it is what stops the object reading as a sticker when the
 * coverflow rotates it (ADDENDUM-7 §1).
 *
 * @param {object} props
 * @param {object} props.m - Decorated market
 * @param {string|null} [props.logo] - Token logo
 */
/**
 * The coin's generated strings, built once per accent and kept.
 *
 * The milling, the denticle ring and the lathe brushing are ~370 interpolated
 * substrings between them, and none of them depend on anything but the accent.
 * Rebuilding them inside the render body meant paying for all three on every
 * frame of a drag or a spin — the coverflow re-renders its whole seat row each
 * time `pos` moves — which is why crypto (the only category on this object)
 * slid noticeably rougher than the others.
 */
const COIN_SURFACES = new Map();

function coinSurfaces(a, D) {
  const hit = COIN_SURFACES.get(a);
  if (hit) return hit;

  // Milled edge: alternating flutes swept around the full turn.
  const NF = 96, st = 360 / NF, flutes = [];
  for (let i = 0; i < NF; i++) {
    const a0 = st * i;
    flutes.push(`${shade(a, -0.5)} ${a0}deg ${a0 + st * 0.5}deg`, `${shade(a, 0.45)} ${a0 + st * 0.5}deg ${a0 + st}deg`);
  }
  const reeding = `conic-gradient(from -25deg, ${flutes.join(",")})`;

  // Denticles: the ring of struck teeth just inside the rim, each a dark dot
  // with a white one offset up-left for the lit edge.
  const ND = 60, RD = D / 2 - 15, teeth = [];
  for (let i = 0; i < ND; i++) {
    const t = (Math.PI * 2 * i) / ND;
    teeth.push(`${(Math.cos(t) * RD).toFixed(1)}px ${(Math.sin(t) * RD).toFixed(1)}px 0 0 ${alpha("#0b1220", 0.34)}`);
    teeth.push(`${(Math.cos(t) * RD - 0.7).toFixed(1)}px ${(Math.sin(t) * RD - 1.1).toFixed(1)}px 0 0 ${alpha("#ffffff", 0.5)}`);
  }

  // Lathe-turned brushing across the recessed field.
  const NR = 150, rays = [];
  for (let i = 0; i < NR; i++) {
    const a0 = (360 / NR) * i;
    rays.push(`${alpha("#ffffff", 0.1)} ${a0}deg ${a0 + 360 / NR / 2}deg`, `${alpha("#0b1220", 0.08)} ${a0 + 360 / NR / 2}deg ${a0 + 360 / NR}deg`);
  }

  const built = { reeding, teeth: teeth.join(","), rays: `conic-gradient(${rays.join(",")})` };
  COIN_SURFACES.set(a, built);
  return built;
}

function Coin3D({ m, logo }) {
  const a = m.accent;
  const D = 158, TH = 26, NL = 16;   // diameter, thickness, wall slices
  const rd = COIN_RELIEF_DEPTH;
  const { reeding, teeth, rays } = coinSurfaces(a, D);

  const disc = (extra) => ({
    position: "absolute", left: "50%", top: "50%", width: D, height: D,
    marginLeft: -D / 2, marginTop: -D / 2, borderRadius: "50%", backfaceVisibility: "hidden", ...extra,
  });

  // The mark is struck into the metal, not pasted on it. Multiply rather than a
  // mask: token logos mix transparent and opaque PNGs, and a mask turns an
  // opaque one into a filled square.
  const markBase = {
    // No `marginBottom`: that 9px cleared the struck ticker, and without it the
    // mark sits dead centre in the field.
    position: "relative", zIndex: 2, width: 62, height: 62,
    backgroundImage: `url("${logo}")`, backgroundSize: "contain",
    backgroundPosition: "center", backgroundRepeat: "no-repeat",
  };
  const mark = COIN_RELIEF === "engraved"
    ? {
        ...markBase, mixBlendMode: "multiply", opacity: 0.9,
        filter: `grayscale(1) contrast(.85) brightness(.9) drop-shadow(0 ${1.2 * rd}px 0 ${alpha("#ffffff", 0.65)}) drop-shadow(0 ${-1 * rd}px ${1.4 * rd}px ${alpha("#0b1220", 0.55)})`,
      }
    : {
        ...markBase, borderRadius: "50%",
        filter: `drop-shadow(0 ${-1 * rd}px 0 ${alpha("#ffffff", 0.55)}) drop-shadow(0 ${2 * rd}px ${1 * rd}px ${alpha("#0b1220", 0.5)}) drop-shadow(0 ${7 * rd}px ${11 * rd}px ${alpha("#0b1220", 0.32)})`,
      };

  return (
    <div style={{ position: "relative", width: D + 34, height: D + 34, transformStyle: "preserve-3d", transform: "rotateX(14deg) rotateY(-24deg)" }}>
      {/* Cast on the floor plane — a `drop-shadow` on the wrapper would flatten
          the whole subtree out of 3D. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", left: "50%", top: "50%", width: D * 0.94, height: TH * 2.4,
          marginLeft: -D * 0.47, marginTop: -TH * 1.2, borderRadius: "50%", pointerEvents: "none",
          transform: `translateY(${D / 2 + 6}px) rotateX(90deg)`,
          background: `radial-gradient(closest-side, ${alpha("#101828", 0.4)}, transparent)`, filter: "blur(7px)",
        }}
      />
      {/* Reverse, so an edge-on turn never shows through. */}
      <div style={disc({ transform: `translateZ(${-TH / 2 - 0.5}px) rotateY(180deg)`, background: `radial-gradient(circle at 34% 28%, ${shade(a, 0.2)}, ${shade(a, -0.45)})` })} />
      {/* The milled wall, sliced through the thickness and brightest mid-way. */}
      {Array.from({ length: NL }, (_, k) => {
        const z = TH / 2 - (TH / (NL - 1)) * k;
        const lit = 1 - Math.abs(z) / (TH / 2);
        return (
          <div
            key={k}
            style={disc({
              background: reeding, opacity: 0.55 + lit * 0.45,
              transform: `translateZ(${z.toFixed(2)}px)`,
              boxShadow: `inset 0 0 ${(10 + lit * 8).toFixed(0)}px ${alpha("#0b1220", 0.4)}`,
            })}
          />
        );
      })}
      {/* Obverse: the struck face, standing proud of the wall. */}
      <div
        style={disc({
          overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
          transform: `translateZ(${TH / 2 + 0.5}px)`,
          background: `radial-gradient(circle at 30% 22%, ${shade(a, 0.8)} 0%, ${shade(a, 0.34)} 30%, ${shade(a, -0.04)} 58%, ${shade(a, -0.34)} 82%, ${shade(a, -0.14)} 100%)`,
          boxShadow: `inset 0 0 0 1px ${alpha("#0b1220", 0.3)}, inset 0 6px 12px ${alpha("#ffffff", 0.5)}, inset 0 -10px 18px ${alpha("#0b1220", 0.34)}`,
        })}
      >
        {/* Field, struck below the rim. */}
        <div
          style={{
            position: "absolute", inset: 20, borderRadius: "50%", overflow: "hidden",
            background: `radial-gradient(circle at 34% 26%, ${shade(a, 0.46)} 0%, ${shade(a, 0.08)} 50%, ${shade(a, -0.3)} 100%)`,
            boxShadow: `inset 0 4px 8px ${alpha("#0b1220", 0.45)}, inset 0 -3px 5px ${alpha("#ffffff", 0.34)}`,
          }}
        >
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: rays, opacity: 0.55, mixBlendMode: "overlay" }} />
        </div>
        {/* Denticles — one 3px dot repeated by box-shadow. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute", left: "50%", top: "50%", width: 3, height: 3, marginLeft: -1.5, marginTop: -1.5,
            borderRadius: "50%", background: "transparent", boxShadow: teeth, pointerEvents: "none",
          }}
        />
        {/* Raised rim, lit from the upper-left and shadowed opposite. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute", inset: 4, borderRadius: "50%", pointerEvents: "none",
            background: `conic-gradient(from 195deg, ${alpha("#ffffff", 0.62)} 0deg, ${alpha("#0b1220", 0.34)} 115deg, ${alpha("#ffffff", 0.5)} 215deg, ${alpha("#0b1220", 0.28)} 325deg, ${alpha("#ffffff", 0.62)} 360deg)`,
            WebkitMaskImage: "radial-gradient(circle, transparent 0 calc(50% - 8px), #000 calc(50% - 8px))",
            maskImage: "radial-gradient(circle, transparent 0 calc(50% - 8px), #000 calc(50% - 8px))",
          }}
        />
        {/* The mark is the whole legend. The struck ticker under it (ADDENDUM-7's
            `coinLegend`) is dropped: with the logo already naming the token it
            read as a caption stamped on the metal. */}
        {logo && <span role="img" aria-label={m.name} style={mark} />}
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, borderRadius: "50%", pointerEvents: "none", mixBlendMode: "screen", background: `radial-gradient(42% 30% at 28% 16%, ${alpha("#ffffff", 0.45)} 0%, transparent 72%)` }} />
        <span className="ce-ms-sheen" style={{ width: "45%", background: "linear-gradient(105deg,transparent 0%,rgba(255,255,255,.7) 50%,transparent 100%)" }} />
      </div>
    </div>
  );
}

/* ── Stocks: the share certificate ──────────────────────────────────────── */

/**
 * Stocks — the trading-floor podium (ADDENDUM-10).
 *
 * Replaces the extruded share certificate entirely. The certificate read as a
 * boxed product rather than as a stock: every other object in the set is the
 * thing itself — a coin, a bar of gold, an oil drum — and equities had no
 * physical object to borrow, so the card stood in for one and looked like
 * packaging.
 *
 * The podium takes the opposite route: the object IS the chart. Extruded
 * columns rise off a deck, with the brand mark and ticker mounted on the board
 * behind them.
 *
 * ADDENDUM-11: the columns were originally the full height of the board and
 * painted in the accent with one green peak. At coverflow size that made the
 * chart the loudest thing on the card and pushed the two elements that actually
 * identify the market — mark and ticker — into a corner. The chart is now a
 * short translucent skyline along the deck, and the mark/ticker are centred and
 * scaled to fill the board. The columns still carry the equities read; they
 * just no longer compete for it.
 *
 * Two structural rules, both learned the hard way and both load-bearing:
 *
 *   1. `stkWrap` must stay `overflow: visible` — it is the `preserve-3d`
 *      container, and clipping it flattens the column side and top faces.
 *   2. The sheen therefore cannot be a direct child of it. At
 *      `translateX(-160%)` it spends most of its cycle up to 129px outside the
 *      object, which is invisible on the light stage but reads as a pale
 *      diagonal slab floating in space on the dark one. It lives inside
 *      `stkBoard`, which already clips. General rule for the set: a sheen needs
 *      a clipping ancestor that is not the preserve-3d wrapper.
 *
 * @param {object} props
 * @param {object} props.m - Decorated market
 * @param {string|null} [props.logo] - Company logo
 */
function Cert3D({ m, logo }) {
  const a = m.accent;
  const W = 200, H = 218, DECK = 26;

  // Column heights are seeded from the market id, not random: the silhouette
  // has to be the same object every render, not reshuffle on each paint.
  let sd = 0;
  for (let i = 0; i < m.id.length; i++) sd = (sd * 31 + m.id.charCodeAt(i)) % 9973;
  const rnd = () => ((sd = (sd * 1103515245 + 12345) % 2147483648) / 2147483648);
  const heights = Array.from({ length: 11 }, (_, i) => {
    const ramp = 9 + (i / 10) * 21;   // a rising trend, left to right
    return Math.round(ramp + rnd() * 7 - 3);
  });

  return (
    <div
      style={{
        position: "relative", width: W, height: H,
        transformStyle: "preserve-3d", transform: "rotateX(8deg) rotateY(-15deg)",
        filter: "drop-shadow(0 18px 24px rgba(11,18,32,.3))",
      }}
    >
      {/* Contact shadow */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", left: 16, right: 6, bottom: -4, height: 18,
          borderRadius: "50%", pointerEvents: "none", filter: "blur(4px)",
          background: "radial-gradient(closest-side, rgba(11,18,32,.34), transparent)",
        }}
      />

      {/* Board — the lit panel everything is mounted on. Clips the sheen. */}
      <div
        style={{
          position: "absolute", inset: `0 0 ${DECK}px 0`,
          borderRadius: "10px 10px 3px 3px", overflow: "hidden",
          background: `linear-gradient(158deg, ${shade(a, 0.5)} 0%, ${shade(a, 0.06)} 42%, ${shade(a, -0.4)} 100%)`,
          boxShadow:
            "inset 0 0 0 1px rgba(255,255,255,.26), inset 0 3px 8px rgba(255,255,255,.34), inset 0 -12px 22px rgba(11,18,32,.4)",
        }}
      >
        {/* A price grid, not decoration — it is what makes the columns read as
            plotted rather than merely stacked. Kept faint: it sits directly
            behind the mark now, and at full strength it dirties the logo. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0, opacity: 0.24,
            backgroundImage:
              "repeating-linear-gradient(180deg, rgba(255,255,255,.12) 0 1px, transparent 1px 16px)," +
              "repeating-linear-gradient(90deg, rgba(255,255,255,.08) 0 1px, transparent 1px 20px)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute", left: "-10%", top: "-30%", width: "80%", height: "90%",
            borderRadius: "50%",
            background: "radial-gradient(closest-side, rgba(255,255,255,.3), transparent)",
          }}
        />
        <span
          className="ce-ms-sheen"
          style={{ top: "-30%", bottom: "-30%", width: "34%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.55),transparent)" }}
        />
      </div>

      {/* Head — mark and ticker, stacked and centred, filling the board. These
          two ARE the object; the columns behind them are texture. The white
          plaque is deliberate: brand marks are drawn for light backgrounds, and
          floating one straight onto a coloured board makes half the set
          unreadable. */}
      <div
        style={{
          position: "absolute", left: 12, right: 12, top: 16, bottom: DECK + 30,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10,
        }}
      >
        <div
          style={{
            width: 76, height: 76, borderRadius: 21, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(160deg, #fff 0%, #fff 62%, #e4e8ef 100%)",
            boxShadow:
              "0 10px 20px rgba(11,18,32,.45), inset 0 -3px 7px rgba(11,18,32,.12), inset 0 2px 0 rgba(255,255,255,.95)",
          }}
        >
          {logo && (
            <img
              src={logo}
              alt=""
              style={{ width: 52, height: 52, borderRadius: 9, objectFit: "contain", filter: "drop-shadow(0 1px 1px rgba(11,18,32,.18))" }}
            />
          )}
        </div>
        <div style={{ minWidth: 0, maxWidth: "100%", textAlign: "center" }}>
          <div
            style={{
              fontSize: 38, fontWeight: 800, letterSpacing: "-.02em", color: "#fff", lineHeight: 1,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "clip",
              textShadow: "0 1px 0 rgba(11,18,32,.55), 0 4px 12px rgba(11,18,32,.5), 0 -1px 0 rgba(255,255,255,.28)",
            }}
          >
            {m.short}
          </div>
          {m.sub && (
            <div
              style={{
                marginTop: 5, fontSize: 7.5, fontWeight: 800, letterSpacing: ".24em",
                textTransform: "uppercase", color: "rgba(255,255,255,.62)",
              }}
            >
              {m.sub}
            </div>
          )}
        </div>
      </div>

      {/* Columns — demoted to a skyline along the deck. They keep the extruded
          language of the set but read as texture behind the mark, not as a
          chart competing with it: short, thin, translucent white rather than
          saturated accent, and no green focal column stealing the eye. */}
      <div
        style={{
          position: "absolute", left: 15, right: 13, bottom: 29, height: 34, opacity: 0.5,
          display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 4,
        }}
      >
        {heights.map((h, i) => (
          <div key={i} style={{ position: "relative", width: 10, height: h, transformStyle: "preserve-3d" }}>
            <div
              aria-hidden="true"
              style={{
                position: "absolute", right: -4, top: -2.5, width: 4, height: h,
                background: "rgba(11,18,32,.26)",
                transform: "skewY(-40deg)", transformOrigin: "left top",
              }}
            />
            <div
              style={{
                position: "absolute", inset: 0, borderRadius: "1.5px 1.5px 0 0",
                background: "linear-gradient(180deg, rgba(255,255,255,.5), rgba(255,255,255,.18))",
              }}
            />
            <div
              aria-hidden="true"
              style={{
                position: "absolute", left: 0, top: -2.5, width: 10, height: 2.5,
                background: "rgba(255,255,255,.72)",
                transform: "skewX(-58deg)", transformOrigin: "bottom left",
              }}
            />
          </div>
        ))}
      </div>

      {/* Deck. The lip was first drawn as a skewX(-58deg) lid to match the
          column tops; it projected past the board's right edge as a visible
          wedge, because a skewed full-width element cannot share a silhouette
          with a rotateY container. Flat lit lip instead — reads as the deck's
          front edge catching light and costs nothing in depth. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", left: 0, right: 0, bottom: 24, height: 5, borderRadius: 1,
          background: `linear-gradient(180deg, ${shade(a, 0.85)}, ${shade(a, 0.24)})`,
          boxShadow: "0 2px 5px rgba(11,18,32,.35)",
        }}
      />
      <div
        style={{
          position: "absolute", left: 0, right: 0, bottom: 0, height: DECK,
          borderRadius: "0 0 5px 5px", padding: "0 10px",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(180deg, ${shade(a, -0.28)}, ${shade(a, -0.56)})`,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,.3), 0 4px 10px rgba(11,18,32,.34)",
        }}
      >
        <span
          style={{
            fontSize: 9, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase",
            color: "rgba(255,255,255,.95)", whiteSpace: "nowrap", overflow: "hidden",
            textOverflow: "ellipsis", maxWidth: "100%",
          }}
        >
          {m.name}
        </span>
      </div>
    </div>
  );
}

/* ── Commodities: the bullion bar ───────────────────────────────────────── */

/**
 * 178 × 100 × 52 of cast metal, extruded with the shared `box` helper.
 *
 * @param {object} props
 * @param {object} props.m - Decorated market
 * @param {string|null} [props.logo] - Metal logo
 */
function Bar3D({ m, logo }) {
  const a = m.accent;
  const B = box(178, 100, 52, a, 3);
  return (
    <div style={B.boxWrap}>
      <div aria-hidden="true" style={B.boxShadow3D} />
      <div aria-hidden="true" style={B.boxBack} />
      <div aria-hidden="true" style={B.boxBottom} />
      <div aria-hidden="true" style={B.boxLeft} />
      <div aria-hidden="true" style={B.boxRight} />
      <div aria-hidden="true" style={B.boxTop} />
      <div
        style={{
          ...B.boxFront, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
          background: `linear-gradient(158deg, ${shade(a, 0.52)} 0%, ${shade(a, 0.1)} 34%, ${shade(a, -0.22)} 66%, ${shade(a, -0.44)} 100%)`,
          boxShadow: `inset 0 4px 7px ${alpha("#ffffff", 0.5)}, inset 0 -7px 13px ${alpha("#0b1220", 0.36)}, inset 3px 0 5px ${alpha("#ffffff", 0.18)}`,
        }}
      >
        <div style={{ textAlign: "center", position: "relative", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          {logo && <img src={logo} alt="" style={{ width: 30, height: 30, objectFit: "contain", opacity: 0.9, filter: `drop-shadow(0 1px 0 ${shade(a, 0.6)})` }} />}
          <div style={{ fontSize: 30, fontWeight: 700, color: shade(a, -0.55), letterSpacing: ".02em", textShadow: `0 1px 0 ${shade(a, 0.55)}`, lineHeight: 1 }}>{m.short}</div>
          <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: ".16em", color: "rgba(0,0,0,.4)" }}>{m.sub}</div>
        </div>
        <span className="ce-ms-sheen" style={{ top: "-30%", bottom: "-30%", width: "34%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.75),transparent)" }} />
      </div>
    </div>
  );
}

/* ── Real estate: the city building ─────────────────────────────────────── */

const HIDE = { display: "none" };

/** Trims pale enough that a signboard painted in them reads as a blank panel. */
const NEAR_WHITE_TRIM = new Set(["#F3EDE3", "#E7DCCB", "#D8C9A3"]);

/**
 * Ink that stays readable on a given background.
 * @param {string} hex - "#rrggbb"
 * @returns {string} Black on light backgrounds, white on dark ones
 */
function textOn(hex) {
  const n = parseInt(String(hex).replace("#", ""), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return r * 0.299 + g * 0.587 + b * 0.114 > 150 ? "#1A1A1A" : "#ffffff";
}

/**
 * Warm interior lights scattered over the window grid, seeded off the city so
 * the same building always lights the same way.
 * @param {string} seed - Market id
 * @returns {{image: string, position: string, size: string}}
 */
function litWindowLayers(seed) {
  let ls = 0;
  for (const ch of String(seed)) ls = (ls * 33 + ch.charCodeAt(0)) % 99991;
  const rnd = () => { ls = (ls * 1103515245 + 12345) % 2147483648; return ls / 2147483648; };

  const image = [];
  const position = [];
  for (let i = 0; i < 14; i++) {
    const warm = rnd() > 0.5 ? "#FFD79A" : "#FFF0C9";
    image.push(`radial-gradient(closest-side, ${alpha(warm, 0.85)}, ${alpha(warm, 0.3)} 60%, transparent)`);
    position.push(`${(8 + rnd() * 84).toFixed(1)}% ${(6 + rnd() * 88).toFixed(1)}%`);
  }
  return { image: image.join(", "), position: position.join(", "), size: "9px 7px" };
}

/**
 * One recognisable object per city, so the coverflow reads without the sidebar.
 *
 * Rooftop pieces stand on the *left neighbour's* roof rather than the main one:
 * the rooftop sign is centred and clamps to the top of the frame on tall
 * towers, so anything on the main roofline collides with it.
 *
 * @param {string} id - Market id
 * @param {object} g - Derived geometry (`left`, `bodyW`, `bodyH`, `roofY`, …)
 * @returns {{lmA: object, lmB: object, lmC: object}} Three absolute layers
 */
function landmarks(id, g) {
  const { trim, left, bodyW, bodyH, groundY, nbL, nbTop } = g;
  const abs = (o) => ({ position: "absolute", ...o });
  const none = { lmA: HIDE, lmB: HIDE, lmC: HIDE };

  const table = {
    // Rooftop water tower, on the neighbour's roof so it clears the sign.
    "new-york": {
      lmA: abs({ left: nbL + 5, bottom: nbTop, width: 28, height: 30, borderRadius: "3px 3px 2px 2px", background: `repeating-linear-gradient(90deg, ${shade("#8A5E3C", 0.18)} 0 3px, ${shade("#6E4A2E", -0.05)} 3px 5px)`, boxShadow: `inset 0 6px 0 -4px ${alpha("#0b1220", 0.35)}, inset 0 -8px 0 -6px ${alpha("#0b1220", 0.35)}` }),
      lmB: abs({ left: nbL + 3, bottom: nbTop + 28, width: 32, height: 14, background: "#4E3320", clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }),
      lmC: abs({ left: nbL + 7, bottom: nbTop - 12, width: 24, height: 13, background: `linear-gradient(74deg, transparent 44%, ${shade("#6E4A2E", -0.1)} 44% 52%, transparent 52%), linear-gradient(-74deg, transparent 44%, ${shade("#6E4A2E", -0.1)} 44% 52%, transparent 52%)` }),
    },
    // Stoop and a street tree at the kerb.
    brooklyn: {
      lmA: abs({ left: left + bodyW - 6, bottom: groundY - 8, width: 34, height: 30, clipPath: "polygon(0 100%, 24% 0, 100% 0, 100% 100%)", background: `repeating-linear-gradient(180deg, ${trim} 0 4px, ${shade(trim, -0.16)} 4px 8px)` }),
      lmB: abs({ left: nbL - 4, bottom: groundY + 14, width: 30, height: 30, borderRadius: "50%", background: `radial-gradient(circle at 36% 30%, ${alpha("#5C8A4A", 0.9)}, ${alpha("#335A2C", 0.9)} 70%)` }),
      lmC: abs({ left: nbL + 9, bottom: groundY - 8, width: 4, height: 24, borderRadius: 2, background: "#6B4A32" }),
    },
    // Leaning palm.
    "los-angeles": {
      lmA: abs({ left: nbL + 6, bottom: groundY - 6, width: 5, height: 96, borderRadius: 3, background: `linear-gradient(180deg, ${shade("#8A6A46", 0.1)}, ${shade("#6B5136", -0.1)})`, transform: "rotate(6deg)", transformOrigin: "bottom center" }),
      lmB: abs({ left: nbL - 12, bottom: groundY + 84, width: 46, height: 30, borderRadius: "50% 50% 8% 8%", background: "conic-gradient(from 200deg at 50% 100%, #3E7A46 0 12%, #4E8F52 12% 24%, #3E7A46 24% 36%, #4E8F52 36% 48%, #3E7A46 48% 60%, transparent 60%)" }),
      lmC: HIDE,
    },
    // Streamline-moderne hotel: corner drum, neon pylon, brow shades.
    "miami-beach": {
      lmA: abs({ left: left - 20, bottom: groundY - 6, width: 40, height: Math.round(bodyH * 0.82) + 30, borderRadius: "20px 4px 0 0", overflow: "hidden", background: `linear-gradient(96deg, ${shade("#FFF1E4", 0.08)} 0 42%, ${shade("#FFD8C2", -0.04)} 42% 100%)`, boxShadow: `inset 0 3px 6px ${alpha("#ffffff", 0.85)}, inset -6px 0 12px ${alpha("#C4705A", 0.35)}, 0 6px 14px ${alpha("#0b1220", 0.12)}` }),
      lmB: abs({ left: left - 6, bottom: groundY + Math.round(bodyH * 0.82) + 18, width: 9, height: 46, borderRadius: 4, background: "linear-gradient(180deg, #FF5E8A, #FF9E5E 52%, #17C8D4)", boxShadow: `0 0 14px 4px ${alpha("#FF5E8A", 0.55)}, 0 0 26px 10px ${alpha("#17C8D4", 0.28)}` }),
      lmC: abs({ left: left + 16, bottom: groundY + 18, width: bodyW - 8, height: Math.round(bodyH * 0.7), borderRadius: "0 6px 0 0", background: `repeating-linear-gradient(180deg, ${alpha("#17C8D4", 0.85)} 0 3px, transparent 3px 6px, ${alpha("#FFF3E2", 0.6)} 6px 8px, transparent 8px 22px)` }),
    },
    // Church steeple rising beside the brick.
    boston: {
      lmA: abs({ left: nbL - 2, bottom: groundY - 6, width: 26, height: Math.round(bodyH * 0.9) + 20, borderRadius: "3px 3px 0 0", background: "linear-gradient(180deg, #F3EDE3, #D7CFC2)" }),
      lmB: abs({ left: nbL - 5, bottom: groundY - 6 + Math.round(bodyH * 0.9) + 20, width: 32, height: 34, background: "#EDE5D8", clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }),
      lmC: abs({ left: nbL + 10, bottom: groundY - 6 + Math.round(bodyH * 0.9) + 54, width: 2, height: 14, background: "#B4A98F" }),
    },
    // Elevated L girder, crossing in front of the building.
    chicago: {
      lmA: abs({ left: 4, right: 4, bottom: groundY + 26, height: 12, background: "linear-gradient(180deg, #6B7280, #3F4650)", boxShadow: `0 3px 6px ${alpha("#0b1220", 0.35)}`, borderRadius: 2 }),
      lmB: abs({ left: 4, right: 4, bottom: groundY - 6, height: 32, background: `repeating-linear-gradient(90deg, ${alpha("#4B525C", 0.85)} 0 4px, transparent 4px 26px)` }),
      lmC: abs({ left: 4, right: 4, bottom: groundY + 38, height: 4, borderRadius: 2, background: `linear-gradient(180deg, ${shade("#93A3B3", 0.2)}, ${shade("#6B7280", -0.1)})` }),
    },
    // Flagpole on the lawn.
    washington: {
      lmA: abs({ left: nbL + 12, bottom: groundY - 6, width: 2, height: 96, background: "#9AA0A6" }),
      lmB: abs({ left: nbL + 14, bottom: groundY + 66, width: 38, height: 26, clipPath: "polygon(0 0, 100% 6%, 100% 94%, 0 100%)", background: "linear-gradient(180deg, #B22234 0 20%, #ffffff 20% 40%, #B22234 40% 60%, #ffffff 60% 80%, #B22234 80% 100%)" }),
      lmC: abs({ left: nbL + 14, bottom: groundY + 80, width: 16, height: 12, background: "#3C3B6E" }),
    },
    // Moonlight tower — lattice mast with a lamp on top.
    austin: {
      lmA: abs({ left: nbL + 2, bottom: groundY - 6, width: 26, height: 118, clipPath: "polygon(38% 0, 62% 0, 100% 100%, 0 100%)", background: `repeating-linear-gradient(28deg, ${alpha("#9AA7B3", 0.8)} 0 1.5px, transparent 1.5px 8px), repeating-linear-gradient(-28deg, ${alpha("#9AA7B3", 0.8)} 0 1.5px, transparent 1.5px 8px)` }),
      lmB: abs({ left: nbL + 9, bottom: groundY + 110, width: 12, height: 12, borderRadius: "50%", background: "#FFF3C4", boxShadow: `0 0 14px 5px ${alpha("#FFF3C4", 0.7)}` }),
      lmC: HIDE,
    },
    // Twin pines against the front range.
    denver: {
      lmA: abs({ left: nbL + 2, bottom: groundY - 6, width: 34, height: 58, clipPath: "polygon(50% 0, 74% 34%, 62% 34%, 86% 68%, 68% 68%, 100% 100%, 0 100%, 32% 68%, 14% 68%, 38% 34%, 26% 34%)", background: alpha("#2F5D3F", 0.9) }),
      lmB: abs({ left: nbL + 30, bottom: groundY - 6, width: 24, height: 40, clipPath: "polygon(50% 0, 100% 100%, 0 100%)", background: alpha("#3A6E4A", 0.85) }),
      lmC: HIDE,
    },
    // Mill smokestacks.
    pittsburgh: {
      lmA: abs({ left: nbL + 4, bottom: groundY - 6, width: 12, height: 78, borderRadius: "2px 2px 0 0", background: "linear-gradient(180deg, #8B5E4A, #5E3E30)", boxShadow: `inset 0 -8px 0 ${trim}` }),
      lmB: abs({ left: nbL + 20, bottom: groundY - 6, width: 10, height: 60, borderRadius: "2px 2px 0 0", background: "linear-gradient(180deg, #8B5E4A, #5E3E30)", boxShadow: `inset 0 -8px 0 ${trim}` }),
      lmC: abs({ left: nbL - 2, bottom: groundY + 74, width: 40, height: 26, borderRadius: "50%", background: alpha("#E6E2DA", 0.7), filter: "blur(4px)" }),
    },
  };

  return table[id] || none;
}

/**
 * Geometry and materials for one city, derived from its `CITY_STYLE` entry.
 * @param {object} c - City params (`a`, `trim`, `tall`, `wide`, `crown`, …)
 * @returns {object} Style objects for every layer of the building
 */
function towerStyles(c, cityName = "", id = "") {
  const W = 230;
  const H = 270;
  const groundY = 42;                                    // plinth + street band
  const bodyH = Math.round((H - groundY - 46) * c.tall);
  const bodyW = Math.round(W * 0.44 * c.wide + 42);
  const left = Math.round((W - bodyW) / 2);
  const bodyTop = H - groundY - bodyH;
  const a = c.a;
  const trim = c.trim;

  const win = {
    grid: `repeating-linear-gradient(90deg, ${alpha("#ffffff", 0.62)} 0 7px, transparent 7px 15px), repeating-linear-gradient(180deg, ${alpha("#0b1220", 0.3)} 0 10px, transparent 10px 18px)`,
    ribbon: `repeating-linear-gradient(180deg, ${alpha("#ffffff", 0.55)} 0 6px, transparent 6px 16px)`,
    arched: `repeating-linear-gradient(90deg, transparent 0 5px, ${alpha("#ffffff", 0.5)} 5px 14px, transparent 14px 20px), repeating-linear-gradient(180deg, ${alpha("#0b1220", 0.26)} 0 12px, transparent 12px 26px)`,
  }[c.win];

  const crowns = {
    spire: {
      crown: { left: left + bodyW / 2 - 22, width: 44, top: bodyTop - 24, height: 26, background: `linear-gradient(180deg, ${trim}, ${shade(a, 0.1)})`, clipPath: "polygon(50% 0, 100% 100%, 0 100%)" },
      mast: { left: left + bodyW / 2 - 2, width: 4, top: bodyTop - 62, height: 40, background: trim, borderRadius: 2 },
    },
    antenna: {
      crown: { left: left + 6, width: bodyW - 12, top: bodyTop - 12, height: 14, background: shade(a, -0.2), borderRadius: "4px 4px 0 0" },
      mast: { left: left + bodyW / 2 - 14, width: 28, top: bodyTop - 56, height: 46, background: `linear-gradient(90deg, ${trim} 0 3px, transparent 3px calc(100% - 3px), ${trim} calc(100% - 3px) 100%)` },
    },
    stepped: {
      crown: { left: left + 14, width: bodyW - 28, top: bodyTop - 26, height: 28, background: `linear-gradient(180deg, ${shade(trim, 0.2)}, ${trim})`, borderRadius: "6px 6px 0 0", boxShadow: `0 8px 0 -2px ${shade(a, 0.15)}` },
      mast: HIDE,
    },
    pitched: {
      crown: { left: left - 8, width: bodyW + 16, top: bodyTop - 30, height: 32, background: shade(a, -0.18), clipPath: "polygon(50% 0, 100% 100%, 0 100%)" },
      mast: { left: left + bodyW - 26, width: 9, top: bodyTop - 52, height: 26, background: trim, borderRadius: 2 },
    },
    dome: {
      crown: { left: left + bodyW / 2 - 34, width: 68, top: bodyTop - 40, height: 44, background: `radial-gradient(circle at 38% 30%, ${shade(trim, 0.45)}, ${trim} 62%, ${shade(trim, -0.25)})`, borderRadius: "68px 68px 0 0" },
      mast: { left: left + bodyW / 2 - 2, width: 4, top: bodyTop - 56, height: 18, background: shade(trim, -0.4), borderRadius: 2 },
    },
    cornice: {
      crown: { left: left - 10, width: bodyW + 20, top: bodyTop - 16, height: 18, background: `linear-gradient(180deg, ${shade(trim, 0.1)}, ${shade(trim, -0.2)})`, borderRadius: 3 },
      mast: HIDE,
    },
    // Austin's raked glass cap.
    angled: {
      crown: { left: left - 4, width: bodyW + 8, top: bodyTop - 26, height: 28, background: `linear-gradient(180deg, ${shade(trim, 0.1)}, ${shade(a, -0.1)})`, clipPath: "polygon(0 100%, 100% 0, 100% 100%)" },
      mast: HIDE,
    },
    flat: {
      crown: { left: left - 4, width: bodyW + 8, top: bodyTop - 10, height: 12, background: shade(a, -0.22), borderRadius: "4px 4px 0 0" },
      mast: HIDE,
    },
    // Any city with an unregistered crown gets a plain parapet rather than a
    // lookup that returns undefined and throws.
  }[c.crown] || { crown: { left, width: bodyW, top: bodyTop - 8, height: 10, background: shade(a, -0.22) }, mast: HIDE };

  const backs = {
    towers: { left: 6, right: 6, bottom: groundY, height: 118, background: `linear-gradient(90deg, transparent 0 6%, ${alpha(a, 0.3)} 6% 17%, transparent 17% 24%, ${alpha(a, 0.22)} 24% 33%, transparent 33% 68%, ${alpha(a, 0.26)} 68% 78%, transparent 78% 86%, ${alpha(a, 0.18)} 86% 95%, transparent 95%)`, maskImage: "linear-gradient(180deg, transparent, #000 40%)", WebkitMaskImage: "linear-gradient(180deg, transparent, #000 40%)" },
    rowhouses: { left: 6, right: 6, bottom: groundY, height: 74, background: `repeating-linear-gradient(90deg, ${alpha(a, 0.26)} 0 22px, transparent 22px 26px)`, borderRadius: "6px 6px 0 0" },
    mountains: { left: 0, right: 0, bottom: groundY, height: 96, background: alpha(a, 0.28), clipPath: "polygon(0 100%, 14% 42%, 26% 66%, 42% 22%, 58% 62%, 72% 34%, 88% 70%, 100% 46%, 100% 100%)" },
    water: { left: 0, right: 0, bottom: groundY - 6, height: 34, background: `repeating-linear-gradient(180deg, ${alpha("#3FA9C9", 0.3)} 0 3px, transparent 3px 8px)`, borderRadius: 6 },
    sun: { left: "50%", marginLeft: -54, bottom: groundY + 40, width: 108, height: 108, borderRadius: "50%", background: `radial-gradient(circle, ${alpha("#F5A623", 0.35)} 0 40%, ${alpha("#F5A623", 0.12)} 60%, transparent 72%)` },
    bridge: { left: 8, right: 8, bottom: groundY + 4, height: 52, background: `linear-gradient(180deg, transparent 46%, ${alpha(a, 0.4)} 46% 54%, transparent 54%)`, borderTop: `3px solid ${alpha(a, 0.35)}`, borderRadius: "50% 50% 0 0 / 60% 60% 0 0" },
    // A low sun over a rippled violet reflection path.
    sunset: { left: 0, right: 0, bottom: groundY - 8, height: 132, borderRadius: 8, background: `radial-gradient(60px 60px at 76% 22%, ${alpha("#FFE08A", 0.95)} 0 42%, ${alpha("#FF9E5E", 0.5)} 58%, transparent 74%), repeating-linear-gradient(180deg, ${alpha("#7A4FA8", 0.26)} 0 3px, transparent 3px 9px)` },
    none: HIDE,
  }[c.back] || HIDE;

  // ── Rooftop sign — the main legibility device ──────────────────────────
  // Placed above whatever crown the city has, so it never collides with a
  // spire, dome or antenna.
  const crownTop = crowns.crown.top ?? bodyTop;
  const mastTop = crowns.mast.top ?? crownTop;
  const signTop = Math.max(4, Math.min(crownTop, mastTop) - 30);
  // Brooklyn, Boston and Pittsburgh have near-white trims. Darkening the board
  // to keep it reading as a board left the lettering illegible, so those keep
  // the pale board and take black lettering instead.
  const pale = NEAR_WHITE_TRIM.has(trim);
  const signInk = trim;
  const long = cityName.length > 10;

  // Positions the landmark table works against.
  const roofY = H - bodyTop;                              // roofline from the frame bottom
  const nbL = Math.max(4, left - 40);                     // left neighbour's x
  const nbTop = groundY - 6 + Math.round(bodyH * 0.52) + 24;
  const lights = litWindowLayers(id || cityName);

  return {
    sky: { position: "absolute", inset: 0, borderRadius: 18, background: `linear-gradient(180deg, ${c.sky} 0%, ${shade(c.sky, 0.55)} 68%, #ffffff 100%)`, overflow: "hidden" },
    // Two dimmer neighbours, deliberately asymmetric, so the object reads as a
    // block rather than a lone tower.
    nbLeft: { position: "absolute", left: Math.max(4, left - 40), width: 38, bottom: groundY - 6, height: Math.round(bodyH * 0.52) + 24, borderRadius: "4px 4px 0 0", overflow: "hidden", background: `linear-gradient(100deg, ${shade(a, -0.1)} 50%, ${shade(a, -0.35)} 55%)` },
    nbLeftWin: { position: "absolute", inset: "8px 6px 0", opacity: 0.4, background: `repeating-linear-gradient(90deg, ${alpha("#ffffff", 0.5)} 0 5px, transparent 5px 11px), repeating-linear-gradient(180deg, ${alpha("#0b1220", 0.3)} 0 9px, transparent 9px 15px)` },
    nbRight: { position: "absolute", right: Math.max(4, left - 52), width: 30, bottom: groundY - 6, height: Math.round(bodyH * 0.38) + 18, borderRadius: "4px 4px 0 0", overflow: "hidden", background: `linear-gradient(100deg, ${shade(a, 0.1)} 45%, ${shade(a, -0.3)} 50%)` },
    nbRightWin: { position: "absolute", inset: "6px 5px 0", opacity: 0.35, background: `repeating-linear-gradient(90deg, ${alpha("#ffffff", 0.5)} 0 4px, transparent 4px 10px), repeating-linear-gradient(180deg, ${alpha("#0b1220", 0.3)} 0 8px, transparent 8px 14px)` },
    signPost: { position: "absolute", left: left + bodyW / 2 - 1, width: 2, top: signTop + 20, height: 14, background: shade(a, -0.4), opacity: 0.8 },
    sign: {
      position: "absolute", left: "50%", transform: "translateX(-50%)", top: signTop, height: 24,
      maxWidth: W - 12, padding: "0 11px", borderRadius: 5,
      display: "flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap",
      background: `linear-gradient(180deg, ${shade(signInk, 0.06)}, ${shade(signInk, -0.2)})`,
      // A pale board needs a dark edge or it dissolves into the sky.
      border: `1.5px solid ${pale ? alpha("#0b1220", 0.3) : alpha("#ffffff", 0.5)}`,
      boxShadow: `0 5px 12px ${alpha("#0b1220", 0.28)}`,
      color: textOn(signInk),
      fontSize: long ? 11 : 13, letterSpacing: long ? ".01em" : ".04em",
      fontWeight: 700, textTransform: "uppercase",
      textShadow: pale ? "none" : `0 1px 0 ${alpha("#0b1220", 0.25)}`,
    },
    // Only tall towers get the marquee running down the facade.
    verticalName: bodyH > 120
      ? { position: "absolute", left: 6, top: 14, writingMode: "vertical-rl", textOrientation: "mixed", fontSize: 12, fontWeight: 700, letterSpacing: ".22em", textTransform: "uppercase", color: alpha("#ffffff", 0.82), textShadow: `0 1px 2px ${alpha("#0b1220", 0.5)}` }
      : HIDE,
    entrance: { position: "absolute", left: bodyW / 2 - 17, bottom: 0, width: 34, height: 26, borderRadius: "5px 5px 0 0", background: `linear-gradient(180deg, ${alpha("#0b1220", 0.42)}, ${alpha("#0b1220", 0.2)})`, boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.3)}` },
    awning: { position: "absolute", left: -7, right: -7, top: -6, height: 7, borderRadius: "3px 3px 0 0", background: trim, opacity: 0.9 },
    pavement: { position: "absolute", left: 6, right: 6, bottom: groundY - 22, height: 7, borderRadius: 4, background: `linear-gradient(180deg, ${alpha("#0b1220", 0.1)}, ${alpha("#0b1220", 0.04)})` },
    backdrop: { position: "absolute", ...backs },
    mast: { position: "absolute", ...crowns.mast },
    crown: { position: "absolute", ...crowns.crown },
    side: { position: "absolute", left: left + bodyW - 2, width: 22, top: bodyTop + 8, height: bodyH - 8, background: `linear-gradient(180deg, ${shade(a, -0.26)}, ${shade(a, -0.46)})`, borderRadius: "0 6px 0 0", transform: "skewY(-9deg)", transformOrigin: "left top" },
    facade: { position: "absolute", left, width: bodyW, top: bodyTop, height: bodyH, borderRadius: "6px 6px 0 0", overflow: "hidden", background: `linear-gradient(100deg, ${shade(a, 0.24)} 0%, ${shade(a, 0.04)} 42%, ${shade(a, -0.2)} 100%)`, boxShadow: `inset 0 3px 5px ${alpha("#ffffff", 0.34)}, inset -6px 0 14px ${alpha("#0b1220", 0.22)}` },
    windows: { position: "absolute", inset: "12px 10px 10px", background: win, opacity: 0.72 },
    // A deterministic scatter of warm interiors — a given city always lights
    // the same way.
    litWindows: { position: "absolute", inset: "12px 10px 10px", opacity: 0.85, pointerEvents: "none", backgroundImage: lights.image, backgroundPosition: lights.position, backgroundSize: lights.size, backgroundRepeat: "no-repeat" },
    mullions: { position: "absolute", inset: 0, opacity: 0.3, pointerEvents: "none", background: `repeating-linear-gradient(90deg, ${alpha("#0b1220", 0.3)} 0 1px, transparent 1px ${Math.max(12, Math.round(bodyW / 5))}px)` },
    beltCourse: bodyH > 96
      ? { position: "absolute", left: 0, right: 0, top: Math.round(bodyH * 0.46), height: 4, background: trim, opacity: 0.5 }
      : HIDE,
    // Foreshortened top face — reads as a solid rather than a flat card.
    roofSlab: { position: "absolute", left: left - 1, width: bodyW + 20, top: bodyTop - 9, height: 18, background: `linear-gradient(180deg, ${shade(a, 0.5)}, ${shade(a, 0.16)})`, transform: "perspective(420px) rotateX(62deg) skewX(-14deg)", transformOrigin: "bottom left", borderRadius: "3px 3px 0 0", boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.5)}` },
    ...landmarks(id, { trim, left, bodyW, bodyH, bodyTop, groundY, roofY, nbL, nbTop }),
    cornice: { position: "absolute", left: 0, right: 0, top: 0, height: 6, background: trim, opacity: 0.85 },
    ground: { position: "absolute", left: 14, right: 14, bottom: groundY - 14, height: 16, borderRadius: 8, background: `linear-gradient(180deg, ${shade(c.sky, -0.1)}, ${shade(c.sky, -0.28)})`, boxShadow: `0 8px 16px ${alpha("#0b1220", 0.18)}` },
    // Secondary now that the rooftop sign carries the name.
    cityPlate: { position: "absolute", left: "50%", transform: "translateX(-50%)", bottom: 0, height: 28, padding: "0 12px", borderRadius: 999, background: "#fff", border: "1px solid #E3E3E3", boxShadow: "0 2px 8px rgba(16,24,40,.1)", display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" },
    // Boston's near-white trim would be unreadable, so it falls back to grey.
    cityTag: { fontSize: 10, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: trim === "#F3EDE3" ? "#8A8A8A" : shade(a, -0.2) },
  };
}

function Tower3D({ m }) {
  const c = m.city;
  const s = towerStyles(c, m.name || "", m.id);
  // Paint order runs back to front: sky, backdrop, neighbours, roofline, the
  // sign, then the building itself, then the street.
  return (
    <div style={{ width: 230, height: 270, position: "relative", filter: "drop-shadow(0 22px 30px rgba(16,24,40,.26))" }}>
      <div style={s.sky} />
      <div style={s.backdrop} />
      <div style={s.nbLeft}><div style={s.nbLeftWin} /></div>
      <div style={s.nbRight}><div style={s.nbRightWin} /></div>
      <div style={s.mast} />
      <div style={s.crown} />
      <div style={s.signPost} />
      <div style={s.sign}>{m.name}</div>
      <div style={s.roofSlab} />
      <div style={s.side} />
      <div style={s.facade}>
        <div style={s.windows} />
        <div style={s.litWindows} />
        <div style={s.mullions} />
        <div style={s.cornice} />
        <div style={s.beltCourse} />
        <div style={s.verticalName}>{m.short}</div>
        <div style={s.entrance}><div style={s.awning} /></div>
      </div>
      <div style={s.lmA} />
      <div style={s.lmB} />
      <div style={s.lmC} />
      <div style={s.ground} />
      <div style={s.pavement} />
      <div style={s.cityPlate}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "#1A1A1A", letterSpacing: ".03em" }}>{m.short}</span>
        <span style={s.cityTag}>{c.tag}</span>
      </div>
    </div>
  );
}

/* ── Watches: the case ──────────────────────────────────────────────────── */

/** Brands whose bezel is fluted; the rest get a brushed conic sweep. */
const FLUTED_BEZEL = new Set(["rolex", "patek-philippe", "cartier", "watch-index"]);

function Watch3D({ m, logo }) {
  const a = m.accent;
  const dial = m.dial || "#1B2733";
  const lightDial = dial === "#F4EFE3";
  const ink = lightDial ? "#1A1A1A" : "#ffffff";

  const bezel = FLUTED_BEZEL.has(m.id)
    ? `repeating-conic-gradient(${shade(a, 0.42)} 0deg 4.5deg, ${shade(a, -0.34)} 4.5deg 9deg)`
    : `conic-gradient(from 210deg, ${shade(a, 0.5)}, ${shade(a, -0.34)} 30%, ${shade(a, 0.42)} 52%, ${shade(a, -0.38)} 78%, ${shade(a, 0.5)})`;

  // Three-link bracelet running off both ends — this is what makes the object
  // read as a watch rather than a medallion.
  const braceletLinks = `repeating-linear-gradient(180deg, ${shade(a, 0.45)} 0 3px, ${shade(a, -0.12)} 3px 9px, ${shade(a, -0.4)} 9px 11px)`;
  const braceletShadow = `inset 0 1px 0 ${alpha("#ffffff", 0.4)}, inset -3px 0 6px ${alpha("#0b1220", 0.3)}`;

  return (
    <div style={{ width: 186, height: 236, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", transformStyle: "preserve-3d", transform: "rotateX(7deg) rotateY(-18deg)" }}>
      {/* Cast on the floor plane. The wrapper's old `drop-shadow` is gone: a
          filter on any ancestor flattens the subtree out of 3D. */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute", left: "50%", bottom: 6, width: 150, height: 30, marginLeft: -75, borderRadius: "50%",
          transform: "translateZ(-14px)", background: `radial-gradient(closest-side, ${alpha("#101828", 0.4)}, transparent)`, filter: "blur(7px)",
        }}
      />
      {/* The case wall, extruded as 12 discs receding in Z and tapering 150 → 144
          so the side reads as a machined case rather than a flat disc. Bracelet,
          lugs and crown stay at Z = 0, so they sit behind it. */}
      {Array.from({ length: 12 }, (_, k) => {
        const t = k / 11;
        const s = 150 - t * 6;
        return (
          <span
            key={k}
            aria-hidden="true"
            style={{
              position: "absolute", left: "50%", top: "50%", width: s, height: s, marginLeft: -s / 2, marginTop: -s / 2,
              borderRadius: "50%", transform: `translateZ(${13 - k * 2.4}px)`,
              background: `linear-gradient(155deg, ${shade(a, 0.3 - t * 0.7)}, ${shade(a, -0.2 - t * 0.4)})`,
            }}
          />
        );
      })}
      <span style={{ position: "absolute", left: "50%", marginLeft: -31, top: 0, width: 62, height: 62, borderRadius: "8px 8px 3px 3px", background: braceletLinks, boxShadow: braceletShadow }} />
      <span style={{ position: "absolute", left: "50%", marginLeft: -31, bottom: 0, width: 62, height: 62, borderRadius: "3px 3px 8px 8px", background: braceletLinks, boxShadow: braceletShadow }} />
      <span style={{ position: "absolute", left: "50%", marginLeft: -38, top: 52, width: 76, height: 26, borderRadius: "14px 14px 6px 6px", zIndex: 1, background: `linear-gradient(180deg, ${shade(a, 0.48)}, ${shade(a, -0.26)})`, boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.45)}` }} />
      <span style={{ position: "absolute", left: "50%", marginLeft: -38, bottom: 52, width: 76, height: 26, borderRadius: "6px 6px 14px 14px", zIndex: 1, background: `linear-gradient(0deg, ${shade(a, 0.48)}, ${shade(a, -0.26)})`, boxShadow: `inset 0 -1px 0 ${alpha("#ffffff", 0.45)}` }} />
      <span style={{ position: "absolute", right: 20, top: "50%", marginTop: -13, width: 12, height: 26, borderRadius: "3px 6px 6px 3px", zIndex: 1, background: `linear-gradient(90deg, ${shade(a, 0.2)}, ${shade(a, -0.42)})` }} />
      <span style={{ position: "absolute", right: 11, top: "50%", marginTop: -8, width: 13, height: 17, borderRadius: 4, zIndex: 1, background: `repeating-linear-gradient(180deg, ${shade(a, 0.4)} 0 2px, ${shade(a, -0.3)} 2px 4px)`, boxShadow: `0 1px 2px ${alpha("#0b1220", 0.4)}` }} />

      <div
        style={{
          position: "relative", width: 150, height: 150, borderRadius: "50%", overflow: "hidden", zIndex: 2,
          display: "flex", alignItems: "center", justifyContent: "center", transform: "translateZ(15px)",
          background: `radial-gradient(circle at 32% 26%, ${shade(a, 0.66)} 0%, ${shade(a, 0.1)} 40%, ${shade(a, -0.26)} 74%, ${shade(a, -0.5)} 100%)`,
          boxShadow: "inset 0 4px 10px rgba(255,255,255,.55), inset 0 -8px 14px rgba(0,0,0,.42)",
        }}
      >
        <div
          style={{
            width: 126, height: 126, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
            background: bezel,
            boxShadow: "inset 0 2px 4px rgba(255,255,255,.45), inset 0 -2px 6px rgba(0,0,0,.42)",
          }}
        >
          <div
            style={{
              width: 106, height: 106, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
              background: `linear-gradient(180deg, ${shade(a, -0.18)}, ${shade(a, -0.42)})`,
              boxShadow: `inset 0 1px 2px ${alpha("#ffffff", 0.3)}`,
            }}
          >
            {/* Dial: minute track, the brand mark, and the crystal. No hands or
                indices — at coverflow scale they read as noise. */}
            <div
              style={{
                position: "relative", width: 96, height: 96, borderRadius: "50%", overflow: "hidden",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: `radial-gradient(circle at 36% 28%, ${shade(dial, 0.34)}, ${dial} 58%, ${shade(dial, -0.32)})`,
                boxShadow: `inset 0 2px 7px rgba(0,0,0,.5), inset 0 0 0 1px ${alpha("#ffffff", 0.12)}`,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: "absolute", inset: 4, borderRadius: "50%", opacity: 0.55, pointerEvents: "none",
                  background: `repeating-conic-gradient(from 0deg, ${alpha(ink, 0.9)} 0deg .8deg, transparent .8deg 6deg)`,
                  maskImage: "radial-gradient(circle, transparent 0 84%, #000 84% 100%)",
                  WebkitMaskImage: "radial-gradient(circle, transparent 0 84%, #000 84% 100%)",
                }}
              />
              {logo && (
                <img
                  src={logo}
                  alt=""
                  style={{ position: "relative", zIndex: 2, width: 56, height: 56, objectFit: "contain", filter: lightDial ? "none" : "brightness(0) invert(1)", opacity: lightDial ? 1 : 0.92 }}
                />
              )}
              <span
                aria-hidden="true"
                style={{
                  position: "absolute", inset: 0, borderRadius: "50%", zIndex: 5, pointerEvents: "none",
                  background: "linear-gradient(128deg, rgba(255,255,255,.3) 0 22%, transparent 34% 100%), radial-gradient(120% 90% at 24% 12%, rgba(255,255,255,.22) 0%, transparent 52%)",
                }}
              />
            </div>
          </div>
        </div>
        <span className="ce-ms-sheen" style={{ width: "40%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.5),transparent)" }} />
      </div>
    </div>
  );
}

/* ── Energy: the oil drum ───────────────────────────────────────────────── */

function Barrel3D({ m, logo }) {
  const a = m.accent;
  const band = m.band || "#E8C33A";

  // Nine stops: dark limbs, a broad gloss left of centre and a second sheen on
  // the right limb. That second highlight is what sells the cylinder.
  const cyl = `linear-gradient(90deg, ${shade(a, 0.1)} 0%, ${shade(a, -0.3)} 6%, ${shade(a, 0.55)} 24%, ${shade(a, 0.95)} 33%, ${shade(a, 0.45)} 44%, ${shade(a, 0.12)} 58%, ${shade(a, 0.5)} 76%, ${shade(a, -0.1)} 90%, ${shade(a, 0.25)} 100%)`;
  // On a near-black body the hoop highlight has to overshoot to read as steel.
  const hoop = {
    position: "absolute", left: -5, right: -5, height: 17, borderRadius: "50%",
    background: `linear-gradient(180deg, ${shade(a, 0.75)} 0 12%, ${shade(a, 1.3)} 22%, ${shade(a, 0.3)} 42%, ${shade(a, -0.5)} 72%, ${shade(a, 0.35)} 100%)`,
    boxShadow: `0 4px 7px ${alpha("#000000", 0.45)}`,
  };

  return (
    <div style={{ width: 196, height: 222, position: "relative", filter: OBJECT_SHADOW }}>
      <div style={{ position: "absolute", left: 34, right: 34, top: 22, bottom: 26, overflow: "hidden", borderRadius: "4px 4px 50% 50% / 4px 4px 12% 12%", background: cyl }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.22, background: `repeating-linear-gradient(180deg, ${alpha("#ffffff", 0.12)} 0 1px, transparent 1px 14px)` }} />
        <div style={{ ...hoop, top: "31%" }} />
        <div style={{ ...hoop, top: "63%" }} />
        {logo && (
          <img src={logo} alt="" style={{ position: "absolute", left: "50%", marginLeft: -11, top: "80%", width: 22, height: 22, objectFit: "contain", filter: "brightness(0) invert(1)", opacity: 0.32 }} />
        )}

        {/* Diamond placard — the ticker sits level inside a rotated frame. */}
        <div
          style={{
            position: "absolute", left: "50%", marginLeft: -37, top: "50%", marginTop: -37, width: 74, height: 74,
            transform: "rotate(45deg)", borderRadius: 6,
            background: `linear-gradient(150deg, ${shade(band, 0.3)} 0%, ${shade(band, 0.02)} 46%, ${shade(band, -0.28)} 100%)`,
            boxShadow: `0 3px 8px ${alpha("#000000", 0.45)}, inset 0 0 0 2px ${alpha("#ffffff", 0.35)}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div style={{ transform: "rotate(-45deg)", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <b style={{ fontSize: 19, fontWeight: 700, letterSpacing: ".04em", color: "#1A1A1A", textShadow: `0 1px 0 ${alpha("#ffffff", 0.45)}` }}>{m.short}</b>
            <span style={{ fontSize: 6, fontWeight: 700, letterSpacing: ".14em", color: alpha("#0b1220", 0.6), whiteSpace: "nowrap" }}>{m.sub}</span>
          </div>
        </div>

        <div style={{ position: "absolute", left: "26%", top: 0, bottom: 0, width: "16%", mixBlendMode: "screen", background: `linear-gradient(90deg, transparent, ${alpha("#ffffff", 0.16)} 50%, transparent)` }} />
        <span className="ce-ms-sheen" style={{ width: "30%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.45),transparent)" }} />
      </div>

      <div style={{ position: "absolute", left: 29, right: 29, bottom: 16, height: 24, borderRadius: "50%", background: `linear-gradient(180deg, ${shade(a, 0.5)} 0 16%, ${shade(a, -0.2)} 46%, ${shade(a, -0.6)} 78%, ${shade(a, 0.2)} 100%)`, boxShadow: `0 4px 8px ${alpha("#000000", 0.4)}` }} />
      <div style={{ position: "absolute", left: 29, right: 29, top: 6, height: 32, borderRadius: "50%", background: `linear-gradient(180deg, ${shade(a, 1.1)} 0 14%, ${shade(a, 0.5)} 30%, ${shade(a, -0.4)} 62%, ${shade(a, 0.2)} 100%)`, boxShadow: `0 5px 10px ${alpha("#000000", 0.35)}` }} />
      {/* The drum reads as open — the dark interior inside the top rim is deliberate. */}
      <div style={{ position: "absolute", left: 38, right: 38, top: 11, height: 22, borderRadius: "50%", background: `linear-gradient(180deg, ${shade(a, -0.65)} 0 44%, ${shade(a, 0.18)} 76%, ${shade(a, 0.5)} 100%)`, boxShadow: `inset 0 3px 5px ${alpha("#000000", 0.6)}`, overflow: "hidden" }}>
        <div style={{ position: "absolute", left: 0, right: 0, top: "56%", height: "40%", background: `linear-gradient(180deg, ${alpha("#ffffff", 0.16)}, transparent)` }} />
      </div>
      <div style={{ position: "absolute", left: "35%", top: 11, width: 11, height: 9, borderRadius: 2, background: `linear-gradient(90deg, ${shade(a, 0.6)}, ${shade(a, -0.3)})` }} />
    </div>
  );
}

/* ── Energy: the gas cylinder ───────────────────────────────────────────── */

function Gas3D({ m, logo }) {
  const a = m.accent;
  const band = m.band || "#2F7CC4";
  return (
    <div style={{ width: 196, height: 236, position: "relative", filter: OBJECT_SHADOW }}>
      {/* The one object in the set that is lit rather than lit-upon. */}
      <div
        style={{
          position: "absolute", left: "50%", marginLeft: -15, top: 0, width: 30, height: 44,
          borderRadius: "50% 50% 46% 46% / 68% 68% 32% 32%",
          background: `radial-gradient(60% 70% at 50% 74%, ${alpha("#EAF6FF", 0.95)} 0 22%, ${alpha(band, 0.9)} 44%, ${alpha("#8FD0FF", 0.5)} 68%, transparent 82%)`,
          filter: "blur(.4px)", boxShadow: `0 0 22px 6px ${alpha(band, 0.35)}`,
        }}
      />
      <div style={{ position: "absolute", left: 40, right: 40, bottom: 8, height: 12, borderRadius: "50%", background: `radial-gradient(closest-side, ${alpha("#0b1220", 0.3)}, transparent)`, filter: "blur(2px)" }} />

      <div style={{ position: "absolute", left: 42, right: 42, top: 62, bottom: 24, overflow: "hidden", borderRadius: "26px 26px 14px 14px", background: `linear-gradient(90deg, ${shade(a, -0.42)} 0%, ${shade(a, 0.1)} 24%, ${shade(a, 0.5)} 40%, ${shade(a, -0.06)} 66%, ${shade(a, -0.45)} 100%)`, boxShadow: `inset 0 -8px 14px ${alpha("#0b1220", 0.3)}` }}>
        <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: 26, borderRadius: "26px 26px 0 0", background: `linear-gradient(180deg, ${alpha("#ffffff", 0.35)}, transparent)` }} />
        <div
          style={{
            position: "absolute", left: 0, right: 0, top: "34%", height: 34,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(180deg, ${shade(band, 0.12)}, ${shade(band, -0.28)})`,
            boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.4)}`,
          }}
        >
          <b style={{ fontSize: 19, fontWeight: 700, letterSpacing: ".06em", color: "#fff", textShadow: `0 1px 2px ${alpha("#0b1220", 0.45)}` }}>{m.short}</b>
          <span style={{ fontSize: 6.5, fontWeight: 700, letterSpacing: ".14em", color: alpha("#ffffff", 0.8), whiteSpace: "nowrap" }}>{m.sub}</span>
        </div>
        {logo && (
          <img src={logo} alt="" style={{ position: "absolute", left: "50%", marginLeft: -12, top: "72%", width: 24, height: 24, objectFit: "contain", opacity: 0.5, filter: `drop-shadow(0 1px 0 ${alpha("#ffffff", 0.5)})` }} />
        )}
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 9, opacity: 0.9, background: `repeating-linear-gradient(115deg, ${shade(band, -0.2)} 0 5px, ${alpha("#F5D06A", 0.95)} 5px 10px)` }} />
        <span className="ce-ms-sheen" style={{ width: "30%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.45),transparent)" }} />
      </div>

      <div style={{ position: "absolute", left: "50%", marginLeft: -26, top: 54, width: 52, height: 14, borderRadius: "6px 6px 3px 3px", background: `linear-gradient(180deg, ${shade(band, -0.1)}, ${shade(band, -0.45)})` }} />
      <div style={{ position: "absolute", left: "50%", marginLeft: -11, top: 40, width: 22, height: 16, borderRadius: 4, background: `linear-gradient(90deg, ${shade(band, 0.1)}, ${shade(band, -0.4)})` }} />

      <div style={{ position: "absolute", right: 34, top: 74, width: 20, height: 20, borderRadius: "50%", background: `radial-gradient(circle at 36% 30%, #fff, ${shade(a, -0.2)})`, boxShadow: `0 0 0 2px ${shade(band, -0.3)}, 0 2px 4px ${alpha("#0b1220", 0.3)}` }}>
        <span style={{ position: "absolute", left: "50%", top: "50%", width: 1.5, height: 7, marginLeft: -0.75, marginTop: -7, background: "#C64A2E", transformOrigin: "50% 100%", transform: "rotate(38deg)" }} />
      </div>
    </div>
  );
}

/**
 * Render whichever object belongs to this market's category.
 *
 * @param {object} props
 * @param {object} props.m - Market decorated by `describeMarket`
 * @param {string|null} [props.logo] - Resolved logo URL, if the object shows one
 *
 * Note there is no off-centre variant. Depth is the coverflow's job — see
 * ADDENDUM-9. An object's material is the same colour in every seat.
 */
function MarketObjectInner({ m, logo }) {
  if (m.shape === "cert") return <Cert3D m={m} logo={logo} />;
  if (m.shape === "bar") return <Bar3D m={m} logo={logo} />;
  if (m.shape === "barrel") return <Barrel3D m={m} logo={logo} />;
  if (m.shape === "gas") return <Gas3D m={m} logo={logo} />;
  if (m.shape === "tower") return <Tower3D m={m} />;
  if (m.shape === "watch") return <Watch3D m={m} logo={logo} />;
  return <Coin3D m={m} logo={logo} />;
}

/**
 * Nothing inside an object depends on the seat it is sitting in — the coverflow
 * moves the wrapper, not the geometry (ADDENDUM-9). So a spin, which re-renders
 * the seat row on every pointer move, has no reason to rebuild the objects
 * inside it. Memoised on identity: `m` comes out of a memoised list and `logo`
 * out of a stable resolver, so this holds for the whole gesture.
 */
export const MarketObject = React.memo(MarketObjectInner);

/**
 * Intrinsic width of each object's wrapper, before the coverflow's scale.
 *
 * The coverflow needs these to space its seats: a flat pitch that suits a 192px
 * coin puts a 232px certificate half on top of its neighbour. Keep in sync with
 * the wrappers below — every value here is that component's outer width.
 */
export const SHAPE_FOOTPRINT = {
  coin: 192,    // D + 34
  cert: 200,    // podium frame (ADDENDUM-10)
  bar: 208,     // box(178, …) → w + 30
  tower: 230,
  watch: 186,
  barrel: 196,
  gas: 196,
};

/** Fallback for a shape with no entry above — the coin, the commonest object. */
export const DEFAULT_FOOTPRINT = 192;

/** Shapes that are real geometry — the coverflow treats these differently. */
/** Shapes that are real geometry — the coverflow treats these differently.
 *
 * ADDENDUM-9: a 3D shape missing from this set silently falls into the flat-tile
 * branch and washes out off-centre. `plate` is listed ahead of any market using
 * it for that reason. */
export const IS_3D_SHAPE = new Set(["coin", "cert", "bar", "watch", "plate"]);

export { Coin3D, Cert3D, Bar3D, Barrel3D, Gas3D, Tower3D, Watch3D };
export default MarketObject;
